import { Grid, Tab } from '@material-ui/core';
import { CustomTabs } from '@ncr-retail/base-retail-components';
import clsx from 'clsx';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import BaseButton from 'src/common/components/base-button';
import { LocalStorage } from 'src/common/helpers/local-storage';
import { IProductDisplayMenu } from 'src/common/interfaces/dtos/IProductDisplayMenu';
import { ISelectedItem } from 'src/common/interfaces/dtos/ISelectedItem';
import { POSModeState } from 'src/common/interfaces/states/POSModeState';
import { ReactComponent as Calculator } from 'src/common/media/Calculator.svg';
import SalesKeypad from 'src/sales-keypad/components/sales-keypad';
import { AppState } from 'src/common/redux/configure-store';
import { styles } from './product-display-layout-styles';
import XLRLogger from 'src/common/helpers/logger';
import Pagination from 'src/pagination/pagination';
import { EJRecordTypes } from 'src/common/enumerations/ej-record-types';
import { useTranslation } from 'react-i18next';
import { IProductDisplayItem } from 'src/common/interfaces/dtos/IProductDisplayItem';
import PLUGridItem from './plu-grid';
import QuickView, { QuickViewToggleButton } from './product-display-quickview';
import { getIsManualWeightDisabled } from 'src/common/device/scale/scale-helper';

const loggerSource = 'components/product-display-layout.tsx';
const PLU_ROW_COUNT = 5;

export type ColumnCount = 2 | 3 | 4 | 6;

const defaultSelectedItem: ISelectedItem = {
  name: '',
  selectedItemId: '',
  unitOfMeasure: '',
  price: 0,
};

const ProductDisplayLayout: React.FC<IProductDisplayLayoutProps> = ({
  productDisplayMenuData,
  onProductSelected,
  onFinish,
  onKeypadSubmit,
  pluTabValue,
  posModeState,
}: IProductDisplayLayoutProps): JSX.Element => {
  const isLeftHanded = useSelector<AppState, boolean>(state => state.cashierPreferences.leftHanded);
  const classes = styles({ leftHanded: isLeftHanded });

  const [resetKeypad, resetKeypadToggle] = useState<boolean>(false);
  const [pluItemsDisabled, setPluItemsDisabled] = useState(false);
  const [selectedProduct, setSelectedProduct] = React.useState<ISelectedItem>(defaultSelectedItem);
  const [topMenuIndex, setTopMenuIndex] = useState<number>(pluTabValue);
  const [subMenuIndex, setSubMenuIndex] = useState<number>(0);

  const [numOfItems, setNumOfItems] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [lastPage, setLastPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [columnCount, setColumnCount] = useState<ColumnCount>(4);

  const [currentParentIndex, setCurrentParentIndex] = useState<number>(0);
  const [isQuickviewVisible, setQuickviewVisibility] = useState(
    LocalStorage.getUserPreferences().productWindowQuickViewVisible,
  );
  const [isKeypadVisible, setKeypadVisibility] = useState(
    LocalStorage.getUserPreferences().productWindowKeypadVisible,
  );

  useEffect((): void => {
    const primaryMenu = productDisplayMenuData[topMenuIndex];
    const subMenu = primaryMenu?.SubMenus?.[subMenuIndex];

    XLRLogger.debug({
      message: `${primaryMenu.Name}, ${subMenu?.Name}: ${selectedProduct.name}`,
      source: loggerSource,
    });
  }, [topMenuIndex, subMenuIndex, selectedProduct, productDisplayMenuData]);

  useEffect((): void => {
    const primaryMenu = productDisplayMenuData[topMenuIndex];
    const subMenu = primaryMenu?.SubMenus?.[subMenuIndex];

    if (subMenu && subMenu.Items) {
      setNumOfItems(subMenu.Items.length);
      setCurrentPage(1);
    }
  }, [topMenuIndex, subMenuIndex, productDisplayMenuData]);

  useEffect(() => {
    let columnCount: ColumnCount = 4;

    if (isQuickviewVisible || isKeypadVisible) {
      if (isQuickviewVisible && isKeypadVisible) {
        columnCount = 2;
      } else {
        columnCount = 3;
      }
    }

    const pageSize = columnCount * PLU_ROW_COUNT;

    if (numOfItems > 0) {
      const lastPage = Math.ceil(numOfItems / pageSize);
      if (lastPage < currentPage) {
        setCurrentPage(lastPage);
      }
      setLastPage(lastPage);
    }

    setColumnCount(columnCount);
    setPageSize(pageSize);
  }, [isQuickviewVisible, isKeypadVisible, numOfItems, currentPage]);

  const handleSelectedProduct = useCallback(
    (selectedProduct: IProductDisplayItem): void => {
      if (selectedProduct.Name && selectedProduct.Id && !pluItemsDisabled) {
        const product: ISelectedItem = {
          name: selectedProduct.Name,
          selectedItemId: selectedProduct.Id,
          unitOfMeasure: selectedProduct.UnitOfMeasure ?? '',
          price: selectedProduct.Price ?? 0,
          quantity: selectedProduct.Quantity,
        };
        setPluItemsDisabled(true);
        setSelectedProduct(product);
        onProductSelected(product).finally(() => {
          resetKeypadToggle(!resetKeypad);
          setPluItemsDisabled(false);
        });
      }
    },
    [onProductSelected, pluItemsDisabled, resetKeypad],
  );

  const toggleKeypad = useCallback((): void => {
    LocalStorage.setUserPreferences({ productWindowKeypadVisible: !isKeypadVisible });
    setKeypadVisibility(!isKeypadVisible);
  }, [isKeypadVisible]);

  const toggleQuickView = useCallback((): void => {
    LocalStorage.setUserPreferences({ productWindowQuickViewVisible: !isQuickviewVisible });
    setQuickviewVisibility(!isQuickviewVisible);
  }, [isQuickviewVisible]);

  const handleTabChange = useCallback(
    (number: number, menuChangeFunction: Function, parentIndex: number) => {
      setCurrentParentIndex(parentIndex);
      menuChangeFunction(number);
    },
    [],
  );

  const handleTopLevelMenuChange = useCallback((newValue: number): void => {
    setTopMenuIndex(newValue);
    setSubMenuIndex(0);
    setSelectedProduct(defaultSelectedItem);
  }, []);

  const handleSubLevelMenuChange = useCallback((newValue: number): void => {
    setSubMenuIndex(newValue);
    setSelectedProduct(defaultSelectedItem);
  }, []);

  const handlePageChange = useCallback(
    (updatedPage: number): void => {
      if (updatedPage > currentPage) {
        setCurrentPage(currentPage + 1);
      } else {
        setCurrentPage(currentPage - 1);
      }
    },
    [currentPage],
  );

  const handleFinish = useCallback((): void => {
    XLRLogger.debug({
      message: 'Product Display Layout Closed',
      source: loggerSource,
    });
    onFinish();
  }, [onFinish]);

  const onSalesKeypadSubmit = useCallback(
    async (input: string): Promise<void> => {
      await onKeypadSubmit(input);
      XLRLogger.debug({
        message: 'Product Display Layout: Keypad Submit',
        source: loggerSource,
      });
    },
    [onKeypadSubmit],
  );

  const handleLeftHandDirection = isLeftHanded ? 'row-reverse' : 'row';

  return (
    <Grid container direction={handleLeftHandDirection}>
      {isQuickviewVisible && (
        <QuickView
          isQuickviewVisible={isQuickviewVisible}
          toggleQuickView={toggleQuickView}
          isLeftHanded={isLeftHanded}
        />
      )}
      <Grid container direction='column' className={classes.mainContainer}>
        <MenuBar
          productDisplayMenuData={productDisplayMenuData}
          menuIndex={topMenuIndex}
          handleTabChange={handleTabChange}
          menuChangeFunction={handleTopLevelMenuChange}
          firstLevel={true}
        />
        <Grid container direction={handleLeftHandDirection} className={classes.pluContainer}>
          <Grid container direction='column' className={classes.pluContents}>
            <MenuBar
              productDisplayMenuData={productDisplayMenuData[topMenuIndex].SubMenus}
              menuIndex={subMenuIndex}
              handleTabChange={handleTabChange}
              menuChangeFunction={handleSubLevelMenuChange}
            />
            <PLUGrid
              menuItems={productDisplayMenuData?.[topMenuIndex]?.SubMenus?.[subMenuIndex].Items}
              handleSelectedProduct={handleSelectedProduct}
              currentPage={currentPage}
              pageSize={pageSize}
              columnCount={columnCount}
              posModeState={posModeState}
            />
            <BottomBar
              isQuickviewVisible={isQuickviewVisible}
              toggleQuickView={toggleQuickView}
              currentPage={currentPage}
              lastPage={lastPage}
              handlePageChange={handlePageChange}
              handleFinish={handleFinish}
              isKeypadVisible={isKeypadVisible}
              toggleKeypad={toggleKeypad}
            />
          </Grid>
          <KeyPad
            currentParentIndex={currentParentIndex}
            isKeypadVisible={isKeypadVisible}
            toggleKeypad={toggleKeypad}
            onSalesKeypadSubmit={onSalesKeypadSubmit}
            resetKeypadToggle={resetKeypad}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};

interface IProductDisplayLayoutProps {
  productDisplayMenuData: IProductDisplayMenu[];
  onProductSelected: (selectedItem: ISelectedItem) => Promise<void>;
  onFinish: () => void;
  onKeypadSubmit: (input: string) => Promise<void>;
  pluTabValue: number;
  posModeState: POSModeState;
}

export default ProductDisplayLayout;

interface KeyPadProps {
  currentParentIndex: number;
  isKeypadVisible?: boolean;
  toggleKeypad: () => void;
  onSalesKeypadSubmit: (input: string) => Promise<void>;
  resetKeypadToggle: boolean;
}

const KeyPad: React.FC<KeyPadProps> = React.memo(
  ({
    currentParentIndex,
    isKeypadVisible,
    toggleKeypad,
    onSalesKeypadSubmit,
    resetKeypadToggle,
  }: KeyPadProps): JSX.Element => {
    const isLeftHanded = useSelector<AppState, boolean>(
      state => state.cashierPreferences.leftHanded,
    );
    const classes = styles();

    const returnModeActive = useSelector(
      (state: AppState): boolean => !!state.posMode.returnMode.returnType,
    );

    const scale = useSelector((state: AppState) => state.scale);

    return (
      <Grid
        container
        className={clsx(classes.salesKeypadContainer, {
          [classes.hidden]: !isKeypadVisible,
          [classes.leftHanded]: isLeftHanded,
        })}
      >
        <Grid item className={classes.salesKeypad} data-testid='all-items-sales-keypad'>
          <Grid item className={classes.salesKeypadFlexContainer}>
            <SalesKeypad
              keyboardId={`product-display-${currentParentIndex}`}
              isKeypadVisible={isKeypadVisible}
              toggleKeypad={toggleKeypad}
              keyPadSubmit={onSalesKeypadSubmit}
              disableManualWeight={getIsManualWeightDisabled(returnModeActive, scale)}
              disableQuantity={false}
              style={{ width: '100%' }}
              resetKeypadToggle={resetKeypadToggle}
            />
          </Grid>
        </Grid>
      </Grid>
    );
  },
);
KeyPad.displayName = 'KeyPad';

const createTabs = (
  menus: IProductDisplayMenu[],
  parentIndex: number,
  tabClassName: string,
  handleTabChange: Function,
  menuChangeFunction: Function,
  scrollButtonClassName: string,
  cssClass?: string,
): JSX.Element => {
  return (
    <CustomTabs
      value={parentIndex}
      onChange={(unused: any, number: number): void =>
        handleTabChange(number, menuChangeFunction, parentIndex)
      }
      className={`fullWidth ${cssClass}`}
      classes={{
        scrollButtons: scrollButtonClassName,
      }}
    >
      {menus.map(
        (menu, index): JSX.Element => {
          return (
            <Tab
              label={menu.Name}
              className={tabClassName}
              centerRipple={true}
              key={index}
              data-testid={`all-items-tab-${menu.Name?.replace(' ', '-')}`}
            />
          );
        },
      )}
    </CustomTabs>
  );
};

interface MenuBarProps {
  productDisplayMenuData?: IProductDisplayMenu[];
  menuIndex: number;
  handleTabChange: Function;
  menuChangeFunction: Function;
  firstLevel?: boolean;
}

const MenuBar: React.FC<MenuBarProps> = React.memo(
  ({
    productDisplayMenuData,
    menuIndex,
    handleTabChange,
    menuChangeFunction,
    firstLevel,
  }: MenuBarProps): JSX.Element => {
    const classes = styles({});

    return productDisplayMenuData ? (
      <Grid container className={classes.pluMenu}>
        <Grid item xs={12}>
          {createTabs(
            productDisplayMenuData,
            menuIndex,
            firstLevel ? classes.tab : classes.secondLevelTab,
            handleTabChange,
            menuChangeFunction,
            classes.scrollButton,
            firstLevel ? classes.categoryBorder : '',
          )}
        </Grid>
      </Grid>
    ) : (
      <></>
    );
  },
);
MenuBar.displayName = 'MenuBar';

interface PLUGridProps {
  menuItems?: IProductDisplayItem[];
  handleSelectedProduct: (selectedProduct: IProductDisplayItem) => void;
  currentPage: number;
  pageSize: number;
  columnCount: ColumnCount;
  posModeState: POSModeState;
}

const PLUGrid: React.FC<PLUGridProps> = React.memo(
  ({
    menuItems,
    handleSelectedProduct,
    currentPage,
    pageSize,
    columnCount,
    posModeState,
  }: PLUGridProps): JSX.Element => {
    const classes = styles({});

    const itemType: EJRecordTypes = posModeState.returnMode.returnType
      ? EJRecordTypes.ReturnItem
      : EJRecordTypes.Product;

    const items = menuItems?.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    return (
      <Grid container className={classes.pluGridContainer}>
        {items ? (
          <Grid container className={classes.pluGrid}>
            {items?.map(item => (
              <PLUGridItem
                key={item.Id}
                product={item}
                columnCount={columnCount}
                handleSelectedProduct={handleSelectedProduct}
                itemType={itemType}
              />
            ))}
          </Grid>
        ) : (
          <></>
        )}
      </Grid>
    );
  },
);

PLUGrid.displayName = 'PLUGrid';
interface BottomBarProps {
  isQuickviewVisible?: boolean;
  toggleQuickView: () => void;
  currentPage: number;
  lastPage: number;
  handlePageChange: Function;
  handleFinish: () => void;
  isKeypadVisible?: boolean;
  toggleKeypad: () => void;
}

const BottomBar: React.FC<BottomBarProps> = React.memo(
  ({
    isQuickviewVisible,
    toggleQuickView,
    currentPage,
    lastPage,
    handlePageChange,
    handleFinish,
    isKeypadVisible,
    toggleKeypad,
  }: BottomBarProps): JSX.Element => {
    const isLeftHanded = useSelector<AppState, boolean>(
      state => state.cashierPreferences.leftHanded,
    );
    const classes = styles({ leftHanded: isLeftHanded });
    const { t } = useTranslation();

    return (
      <Grid
        container
        className={classes.bottomBar}
        direction={isLeftHanded ? 'row-reverse' : 'row'}
      >
        <Grid item className={classes.bottomBarGroup}>
          {!isQuickviewVisible && (
            <Grid item>
              <QuickViewToggleButton
                toggleQuickView={toggleQuickView}
                isQuickviewVisible={isQuickviewVisible}
              />
            </Grid>
          )}
          <Grid
            container
            justifyContent='center'
            alignItems='center'
            className={clsx(
              classes.paginationContainer,
              { [classes.smallerPaginationpadding]: isQuickviewVisible },
              'fullHeight',
            )}
          >
            <Grid container justifyContent='center' className='fullHeight'>
              <Pagination
                currentPage={currentPage}
                totalPages={lastPage}
                updateCurrentPageHandler={handlePageChange}
                testIdPrefix={'plu-products'}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid
          item
          className={classes.bottomBarGroup}
          direction={isLeftHanded ? 'row-reverse' : 'row'}
        >
          <Grid item>
            <BaseButton
              data-testid='all-items-finish'
              className={classes.finishButton}
              onClick={handleFinish}
            >
              <span className='label'>{t('Finish')}</span>
            </BaseButton>
          </Grid>
          {!isKeypadVisible && (
            <Grid item>
              <BaseButton
                data-testid='all-items-keypad-button'
                isSquare
                className={clsx(classes.keypadButton, { [classes.leftHanded]: isLeftHanded })}
                onClick={toggleKeypad}
              >
                <Calculator />
              </BaseButton>
            </Grid>
          )}
        </Grid>
      </Grid>
    );
  },
);

BottomBar.displayName = 'PLUGrid';
